import {Formik, FormikProps} from 'formik';
import React, {ReactElement, useContext, useEffect} from 'react';
import {PhaseName} from '../../../../../constants/api-urls';
import usePost from '../../../../../shared/hooks/use-post.hook';
import usePut from '../../../../../shared/hooks/use-put.hook';
import {PhaseValidationResult} from '../../../../../shared/model/phase-validation-result.model';
import {Phase} from '../../../../../shared/model/phase.model';
import {ApiHelper} from '../../../../../utils/api-helper';
import {AppSnackbarContext} from '../../../../shared/app-snackbar-provider/AppSnackbarProvider';
import {DraggableCheckGroup, toCheckGroups} from '../../../shared/draggable-checks/draggable-checks.model';
import DraggableChecks from '../../../shared/draggable-checks/DraggableChecks';
import {
  KeyedApprovalRule,
  toApprovalRule
} from '../../loan-configuration-step/approval-configuration/approval-rule-items/keyed-approval-rules.model';
import LoanConfigurationStep from '../../loan-configuration-step/LoanConfigurationStep';
import {LoanConfigurationContext} from '../../LoanConfiguration';
import {getInitialApprovalRulesValues} from '../hooks/use-initial-approval-rules-values.hook';
import { getCheckFieldValidationSchema } from '../../../../../utils/step-form-utils/validation-schema/check-field-validation-schema';

interface FormFields {
  approvalRules?: KeyedApprovalRule[];
  isApprovalRequired?: boolean;
}

interface Props {
  config: Phase;
  checkGroups: DraggableCheckGroup[];
  updateCheckGroups: (checkGroups: DraggableCheckGroup[]) => void;
}

const CustomPhaseForm = ({config, checkGroups, updateCheckGroups}: Props): ReactElement => {

  const {showErrorMessage} = useContext(AppSnackbarContext);
  const {process, setIsSaveDisabled} = useContext(LoanConfigurationContext);
  const phaseUrl = ApiHelper.getPhaseConfigurationUrl(process.id, PhaseName.CUSTOM);
  const updatePhase = usePut<void, Phase>(phaseUrl);
  const validatePhase = usePost<PhaseValidationResult, Phase>(`${phaseUrl}/validate`);

  const buildPhaseConfig = (formFields: FormFields): Phase | undefined => ({
    ...config,
    approvalRules: formFields.isApprovalRequired ? formFields.approvalRules?.map(toApprovalRule) : [],
    checkGroups: toCheckGroups(checkGroups)
  });

  const handleSave = async (formFields: FormFields): Promise<void> =>
    await updatePhase(buildPhaseConfig(formFields)).catch(error => showErrorMessage(error.message));

  const validateChecks = (checkGroups: DraggableCheckGroup[]) => {
    getCheckFieldValidationSchema().validate(checkGroups, {abortEarly: false})
      .then(() => {
        setIsSaveDisabled(false);
      })
      .catch((err) => {
        setIsSaveDisabled(true);
      });
  };

  const onSetGroups = async (checkGroups: DraggableCheckGroup[]): Promise<void> => {
    validateChecks(checkGroups);
    updateCheckGroups(checkGroups);
  };

  // Handles existing checks that have no action owners or name 
  useEffect(() => {
    validateChecks(checkGroups);
  }, []);


  return (
    <Formik<FormFields> initialValues={getInitialApprovalRulesValues(config)} onSubmit={handleSave}>
      {
        ({values, submitForm}: FormikProps<FormFields>): ReactElement => {

          const validate = (): Promise<PhaseValidationResult> => validatePhase(buildPhaseConfig(values));

          return <LoanConfigurationStep handleSave={submitForm} validationFunction={validate}>
            <DraggableChecks checkGroups={checkGroups}
                             setCheckGroups={onSetGroups}
                             phaseId={config.id} />
          </LoanConfigurationStep>;
        }
      }
    </Formik>
  );
};

export default CustomPhaseForm;
